<script setup lang="ts">

import PayoutHintPanel from "@/components/forms/payout/PayoutHintPanel.vue";
import PayoutFormGroup from "@/components/forms/payout/PayoutFormGroup.vue";
import {computed, PropType, ref, watch} from "vue";
import numbro from "numbro";
import {CurrencyCode} from "@/models/core/currency";
import {debounce} from "lodash";
import {MoneyFormatFilter} from "@/models/filters/money";
import {PayoutWebModel} from "capitalist-shared/src/finance/payments";


const props = defineProps({
  model: {
    type: Object as PropType<PayoutWebModel>,
    required: true
  },
  destCurrency: {
    type: String,
    default: null
  },
  removeKopecks: {
    type: Boolean,
    default: false
  },
  correctResultAmountFunction: {
    type: Function as PropType<(amount: number) => number>,
    default: (amount: number) => amount
  }
})

const model = computed(() => props.model)
const removeKopecks = computed(() => props.removeKopecks)

const isDirty = ref(false)
const isCalculating = ref(false)
const estimated = ref<{
  amount: number,
  srcAmount: number | string,
  srcCurrency: string,
  currency: string,
} | null>(null)

const srcAmount = computed(() => {
  return numbro(props.model?.values.amount).value();
})

const srcCurrency = computed(() => {
  return model.value?.getSourceAccount()?.currency;
})

const destCurrency = computed(() => {
  if (props.destCurrency) return props.destCurrency;
  const modelDestCurrency = props.model.getDestCurrency() || [];
  if (modelDestCurrency.length == 0)
    return null;

  if (modelDestCurrency.includes)

  if (!props.model.options.destCurrency || (props.model.options.destCurrency.length == 0)) return null;
  if (props.model.options.destCurrency.indexOf(srcCurrency.value) != -1)
    return null;
  return props.model.options.destCurrency[0];
})



const isValidData = computed(() => {
  return srcAmount.value
    && srcCurrency.value
    && destCurrency.value
    && (srcCurrency.value !== destCurrency.value)
    && (srcAmount.value > 0);
})

const showCalculating = computed(() => isCalculating.value || isDirty.value)

const showBlock = computed(() =>
  model.value && isValidData.value && (isDirty.value || isCalculating.value || estimated.value) && (!estimated?.value || estimated?.value?.amount > 0)
)

const trimmedAmount = computed(() => {
  return String('' + model.value?.values.amount).trim();
})

const calculateAmountSourceFunction = (dc: string = null) => {
  // console.log('calculateAmountSourceFunction', dc, destCurrency.value, props.destCurrency);

  if (!isValidData.value) {
    return;
  }

  isCalculating.value = true;
  estimated.value = null;

  model.value.evaluateDestinationAmount(dc).then((res) => {
    const result = {...res}
    if (removeKopecks.value
      && (estimated?.value.currency === CurrencyCode.RUR)) {
      result.amount = Math.floor(result?.amount);
    }
    if (props.correctResultAmountFunction) {
      result.amount = props.correctResultAmountFunction?.(result.amount);
    }
    estimated.value = result;
    isCalculating.value = false;
    isDirty.value = false;
    return estimated.value;
  }).catch((err ) => {
    isCalculating.value = false;
    isDirty.value = false;
    //throw err;
  });
}

const calculateAmount = debounce(calculateAmountSourceFunction, 500);

watch([
  trimmedAmount,
  destCurrency,
  srcCurrency
], () => {
  // console.log('watch', trimmedAmount.value, destCurrency.value, srcCurrency.value);
  isDirty.value = true;
  calculateAmount(destCurrency.value);
})

</script>

<template>
  <payout-form-group v-if="showBlock">
    <div class="form-control-space">
      <payout-hint-panel>
        <template v-if="showCalculating">
          <i18n path="calculating_in_currency" tag="span">
            <template v-slot:currency><strong>
              <nobr>{{ destCurrency }}</nobr>
            </strong></template>
          </i18n>
        </template>
        <template v-if="estimated && !isDirty">
          <i18n path="result_message" tag="span">
            <template v-slot:srcamount>
              {{ MoneyFormatFilter(estimated.srcAmount, estimated.srcCurrency) }}
            </template>
            <template v-slot:srccurrency>{{ estimated.srcCurrency }}</template>
            <template v-slot:dstamount>{{
                MoneyFormatFilter(estimated.amount, estimated.currency)
              }}
            </template>
            <template v-slot:dstcurrency> {{ estimated.currency }}</template>
          </i18n>
        </template>
      </payout-hint-panel>
    </div>
  </payout-form-group>
</template>

<style scoped lang="less">

</style>


<i18n>
{
  "en": {
    "calculating_in_currency": "Calculating expected amount in {currency} currency...",
    "result_message": "Estimated amount of {srcamount} {srccurrency} payment is {dstamount} {dstcurrency}."
  },
  "ru": {
    "calculating_in_currency": "Подсчитываю ожидаемую сумму зачисления в валюте {currency}...",
    "result_message": "Ожидаемая сумма зачисления для платежа {srcamount} {srccurrency} на момент отправки: {dstamount} {dstcurrency}."
  }
}
</i18n>
